import React, { useEffect } from 'react';
import { ProjectAddress, ProjectAddress_DB } from '@models/Models';
import { Row, Col, Form } from 'react-bootstrap';
import { AiOutlineDelete, AiOutlineSave, AiOutlineLoading } from "react-icons/ai";
import useAddress from '@hooks/useAddress';

interface AddressRowProps {
    item: ProjectAddress;
    onRemove: (index: number) => void;
    onAddressSave: (updatedAddress: ProjectAddress, id: string) => Promise<void>;
    index: number;
}

const AddressRow: React.FC<AddressRowProps> = ({ item, onRemove, onAddressSave, index }) => {
    const [updatedAddress, setUpdatedAddress] = React.useState<ProjectAddress>(item);
    const { updateAddressRow } = useAddress();
    const [loading, setLoading] = React.useState(false);

    useEffect(() => {
        setUpdatedAddress(item);
    }, [item]);

    const hasChanges = (
        Object.keys(item) as Array<keyof ProjectAddress>
    ).some(field => updatedAddress[field] !== item[field]);

    const handleChange = (field: keyof ProjectAddress, value: string) => {
        const newAddress = { ...updatedAddress, [field]: value };
        setUpdatedAddress(newAddress);
    };

    const handleSave = async() => {
        try {
            setLoading(true);

            const updatedAddress_DB: ProjectAddress_DB = {
                street: updatedAddress.street,
                zipCode: updatedAddress.zipCode,
                city: updatedAddress.city,
                hausnummer: updatedAddress.hausnummer,
                stiege: updatedAddress.stiege,
                country: updatedAddress.country,
            }

            const result = await updateAddressRow(updatedAddress_DB, item.id);

            if(!result.success) {
                console.log("Error updating address: ", result.message);
                alert("Error updating address: " + result.message);
                return;
            }

            const savedAddress = result.data as ProjectAddress;
            await onAddressSave(savedAddress, item.id);
            setUpdatedAddress(savedAddress);
        }finally {
            setLoading(false);
        }
    }


    return (
        <Form>
            <Row className='mb-2'>
                <Col lg={8} className='myBorder p-0'>
                    <Row className='d-flex align-items-center'>
                        <Col>
                            <Form.Control
                                value={updatedAddress.street}
                                onChange={(e) => handleChange("street", e.target.value)}
                                className='rounded-0'
                            />
                        </Col>
                        <Col xs={2} className='p-0'>
                            <Form.Control
                                value={updatedAddress.zipCode}
                                onChange={(e) => handleChange("zipCode", e.target.value)}
                                className='rounded-0'
                            />
                        </Col>
                        <Col>
                            <Form.Control
                                value={updatedAddress.city}
                                onChange={(e) => handleChange("city", e.target.value)}
                                className='rounded-0'
                            />
                        </Col>
                    </Row>
                </Col>
                <Col lg={2} className='myBorder p-0'>
                    <Form.Group>
                        <Form.Control
                            value={updatedAddress.hausnummer}
                            onChange={(e) => handleChange("hausnummer", e.target.value)}
                        />
                    </Form.Group>
                </Col>
                <Col lg={2} className='myBorder p-0 d-flex align-items-center'>
                    <Form.Group>
                        <Form.Control
                            value={updatedAddress.stiege}
                            onChange={(e) => handleChange("stiege", e.target.value)}
                        />
                    </Form.Group>

                    {hasChanges &&
                        <button
                            type="button"
                            className="address-save-button"
                            onClick={() => handleSave()}
                            aria-label="Adresse speichern"
                            title="Adresse speichern"
                        >
                            <AiOutlineSave aria-hidden="true" />
                        </button>
                    }

                    <button
                        type="button"
                        className="address-remove-button"
                        onClick={() => onRemove(index)}
                        aria-label="Adresse entfernen"
                        title="Adresse entfernen"
                    >
                        <AiOutlineDelete aria-hidden="true" />
                    </button>

                    {loading && 
                        <AiOutlineLoading 
                            aria-hidden="true"
                        />
                    }
                </Col>
            </Row>
        </Form>
    );
};

export default AddressRow;
